import app from 'flarum/forum/app';
import DiscussionList from 'flarum/forum/components/DiscussionList';
import { DeckDiscussionListState, asQuery, refillIfEmpty } from '../states/deckListStates';
import { DISCUSSION_RESTORED, POSTED, POST_RESTORED, REMOVED, STARTED, TAGGED, discussionOf, postOf, relatedId } from './realtimeEvents';

/** Enough to say "20 new"; nobody needs an exact count past a screenful. */
const CHECK_LIMIT = 20;

/**
 * A column backed by its own DiscussionListState, so any filter the API
 * understands (including other extensions' filters) works without the client
 * having to decide what belongs in the column.
 */
export default class DiscussionListSource {
  /** The newest `lastPostedAt` on screen; checks only ask for activity after it. */
  key = null;

  /**
   * @param belongs Whether a discussion still belongs here, for columns that can
   *                tell from the store (Unread: whether it's still unread), so
   *                one can leave without asking the server.
   */
  constructor(params, belongs) {
    this.belongs = belongs;
    this.state = new DeckDiscussionListState(params);
  }
  prune() {
    if (!this.belongs) return;
    this.state.getPages().flatMap(page => page.items).filter(discussion => !this.belongs(discussion)).forEach(discussion => this.removeFromList(discussion));
    refillIfEmpty(this.state);
  }
  load() {
    if (this.state.hasItems()) return Promise.resolve();
    return this.prepare().then(() => this.state.refresh()).then(() => this.snapshot());
  }

  /** Runs once before the first load, for columns whose params need looking up. */
  prepare() {
    return Promise.resolve();
  }
  view() {
    return m(DiscussionList, {
      state: this.state
    });
  }

  /**
   * The column's own filters plus `lastPostedAfter`: a range on an indexed
   * column that usually matches nothing, rather than re-running the list.
   */
  async checkForNew() {
    let latest;
    if (this.key) {
      latest = await this.activeSinceKey();
    } else {
      // An empty column has no key yet; its query is cheap by definition.
      latest = await this.state.loadPage(1);
    }
    return latest.filter(discussion => discussion.lastPostedUser() !== app.session.user).length;
  }
  showNew() {
    return this.state.revalidate().then(() => this.snapshot());
  }

  /**
   * A reply to a discussion already in the column moves it to the top straight
   * from the payload: a new reply doesn't change which column a discussion
   * belongs in. Anything the column doesn't hold yet (a new discussion, a
   * reply elsewhere that may now match) only the server can place.
   */
  onRealtime(event) {
    const discussion = discussionOf(event);
    if (!discussion) return;
    const shown = this.shows(discussion);
    if (event.name === REMOVED) {
      if (event.payload.data.type !== 'discussions' || !shown) return;
      this.removeFromList(discussion);
      return 'updated';
    }

    // Only the server knows where something brought back belongs.
    if (event.name === DISCUSSION_RESTORED || event.name === POST_RESTORED && !shown) return 'check';
    if (event.name === POSTED || event.name === STARTED) {
      // The member's own posts can change membership (e.g. Unread), so ask.
      const subject = postOf(event) ?? discussion;
      const own = relatedId(subject, 'user') === app.session.user?.id();
      if (shown && !own && this.sortsByActivity()) {
        this.moveToTop(discussion);
        this.advanceKey(discussion);
        return 'inserted';
      }
      return 'check';
    }

    // Re-tagging can move a discussion into, or out of, a tag's column.
    if (event.name === TAGGED) return 'check';

    // Edits, renames, likes, locking, stickying: the store already has it.
    return shown ? 'updated' : undefined;
  }

  /** Within this column; realtime's own handler takes it off every other list. */
  removeFromList(discussion) {
    const state = this.state;
    [...state.pages, {
      items: state.extraDiscussions
    }].forEach(page => {
      const index = page.items.findIndex(item => item.id() === discussion.id());
      if (index !== -1) page.items.splice(index, 1);
    });
  }
  shows(discussion) {
    return this.state.getPages().some(page => page.items.some(item => item.id() === discussion.id()));
  }

  /** "To the top" only means something when the column is ordered by activity. */
  sortsByActivity() {
    const sort = this.state.currentSort();
    return !sort || sort === '-lastPostedAt';
  }
  advanceKey(discussion) {
    const lastPostedAt = discussion.lastPostedAt();
    if (lastPostedAt && (!this.key || lastPostedAt > this.key)) this.key = lastPostedAt;
  }
  async applyNew() {
    if (!this.key || !this.sortsByActivity()) return null;
    const latest = await this.activeSinceKey();

    // Oldest first, so the most recent activity ends up on top.
    [...latest].sort((a, b) => (a.lastPostedAt()?.getTime() ?? 0) - (b.lastPostedAt()?.getTime() ?? 0)).forEach(d => this.moveToTop(d));
    latest.forEach(discussion => {
      const lastPostedAt = discussion.lastPostedAt();
      if (lastPostedAt && lastPostedAt > this.key) this.key = lastPostedAt;
    });
    return latest.length;
  }

  /**
   * The column's own request, includes and all, narrowed to activity since the
   * key: a range on an indexed column that usually matches nothing.
   */
  activeSinceKey() {
    const params = this.state.requestParams();
    return app.store.find('discussions', {
      ...asQuery(params),
      filter: {
        ...params.filter,
        lastPostedAfter: this.key.toISOString()
      },
      page: {
        limit: CHECK_LIMIT,
        total: 0
      }
    });
  }

  /**
   * `DiscussionListState.addDiscussion()` can't be used here: it removes the
   * discussion through an emitter shared by every list, so it would vanish
   * from the other columns and the index. This moves it within this list only.
   */
  moveToTop(discussion) {
    const state = this.state;
    const id = discussion.id();
    [...state.pages, {
      items: state.extraDiscussions
    }].forEach(page => {
      const index = page.items.findIndex(item => item.id() === id);
      if (index !== -1) page.items.splice(index, 1);
    });
    state.extraDiscussions.unshift(discussion);
  }
  snapshot() {
    this.key = null;
    this.state.getPages().flatMap(page => page.items).forEach(discussion => {
      const lastPostedAt = discussion.lastPostedAt();
      if (lastPostedAt && (!this.key || lastPostedAt > this.key)) this.key = lastPostedAt;
    });
  }
}
flarum.reg.add('flarum-deck', 'forum/columns/DiscussionListSource', DiscussionListSource);